import axios from "axios";
import { API_BASE_URL } from "../config";

/**
 * Run Gate-1 system connectivity check via backend gate0Validate.
 * Uses apiAgent (LLM-assisted) when apiAgent/agent.py is present; otherwise HTTP probe fallback.
 * @param {object} appConfig
 * @param {{ projectId?: string|number, gatePhase?: string, endpointIds?: string[] }} [options]
 */
function filterAppConfigEndpoints(appConfig, endpointIds) {
  if (!Array.isArray(endpointIds) || endpointIds.length === 0) {
    return appConfig;
  }
  const idSet = new Set(endpointIds.map(String));
  return {
    ...appConfig,
    endpoints: (appConfig.endpoints || []).filter((ep) => idSet.has(String(ep.id))),
  };
}

export async function runSystemConnectivityCheck(appConfig, options = {}) {
  const endpointIds = Array.isArray(options.endpointIds) ? options.endpointIds : [];
  const filteredConfig =
    endpointIds.length > 0
      ? filterAppConfigEndpoints(appConfig, endpointIds)
      : appConfig;

  const body = { app_config: filteredConfig };
  if (endpointIds.length) {
    body.endpoint_ids = endpointIds;
  }
  if (options.projectId != null) {
    body.project_id = options.projectId;
  }
  if (options.gatePhase) {
    body.gate_phase = options.gatePhase;
  }
  const res = await axios.post(`${API_BASE_URL}/readiness/system-check`, body);
  return res.data;
}

/**
 * Run Gate-1 SQL database connectivity check.
 * @param {object} dbConfig
 * @param {{ projectId?: string|number }} [options]
 */
export async function runDataConnectivityCheck(dbConfig, options = {}) {
  const body = { db_config: dbConfig };
  if (options.projectId != null) {
    body.project_id = options.projectId;
  }
  const res = await axios.post(`${API_BASE_URL}/readiness/data-check`, body);
  return res.data;
}

/**
 * Run Gate-1 vector-store similarity-search readiness check.
 * @param {object} vectorStoreConfig
 * @param {{ projectId?: string|number }} [options]
 */
export async function runVectorStoreCheck(vectorStoreConfig, options = {}) {
  const body = { vector_store_config: vectorStoreConfig };
  if (options.projectId != null) {
    body.project_id = options.projectId;
  }
  const res = await axios.post(`${API_BASE_URL}/readiness/vector-store-check`, body);
  return res.data;
}

/**
 * Run Gate-1 traces / observability readiness check.
 * @param {object} tracesConfig
 * @param {{ projectId?: string|number }} [options]
 */
export async function runTraceCheck(tracesConfig, options = {}) {
  const body = { traces_config: tracesConfig };
  if (options.projectId != null) {
    body.project_id = options.projectId;
  }
  const res = await axios.post(`${API_BASE_URL}/readiness/trace-check`, body);
  return res.data;
}

/** One in-flight evidence GET per project — StrictMode/remount reuse the same resolve. */
const evidenceInflight = new Map();

function isCanceledEvidenceRequest(err) {
  return Boolean(
    axios.isCancel?.(err) ||
      err?.code === "ERR_CANCELED" ||
      err?.name === "CanceledError"
  );
}

/**
 * Resolve Gate 1 declared-check evidence from intake captured {text, value}.
 * Infra failure returns unavailable (not Missing).
 * Cancellation is not treated as unavailable.
 * @param {string|number} projectId
 * @param {{ signal?: AbortSignal }} [options]
 */
export function fetchReadinessEvidence(projectId, options = {}) {
  const key = String(projectId ?? "");
  if (!key) {
    return Promise.resolve({
      success: false,
      unavailable: true,
      error: "projectId is required",
      data: { results: {}, diagnostics: { unavailable: true } },
    });
  }

  const existing = evidenceInflight.get(key);
  if (existing) return existing;

  const request = (async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/projects/${key}/gate1/readiness-evidence`,
        options.signal ? { signal: options.signal } : undefined
      );
      const body = res.data || {};
      const results = body.data?.results;
      return {
        success: body.success !== false,
        unavailable: Boolean(body.unavailable) || body.success === false,
        data: {
          results: results && typeof results === "object" ? results : {},
          diagnostics: body.data?.diagnostics || {},
        },
      };
    } catch (err) {
      if (isCanceledEvidenceRequest(err)) {
        return {
          success: false,
          cancelled: true,
          unavailable: false,
          error: err.message || "cancelled",
          data: { results: {}, diagnostics: {} },
        };
      }
      return {
        success: false,
        unavailable: true,
        error: err.response?.data?.error || err.message || "unavailable",
        data: { results: {}, diagnostics: { unavailable: true } },
      };
    }
  })();

  evidenceInflight.set(key, request);
  request.finally(() => {
    if (evidenceInflight.get(key) === request) evidenceInflight.delete(key);
  });
  return request;
}

const EMPTY_DISCOVERY = {
  systemType: "unknown",
  accessMethods: [],
  providers: { api: null, database: null, vectorStore: null },
};

/**
 * Gate 1 DiscoveryResult from Gate 0 intake (informational; does not hide surfaces).
 * @param {string|number} projectId
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function fetchReadinessDiscovery(projectId, options = {}) {
  const key = String(projectId ?? "");
  if (!key) {
    return {
      success: false,
      error: "projectId is required",
      data: { ...EMPTY_DISCOVERY, providers: { ...EMPTY_DISCOVERY.providers } },
    };
  }

  try {
    const res = await axios.get(
      `${API_BASE_URL}/projects/${key}/gate1/discovery`,
      options.signal ? { signal: options.signal } : undefined
    );
    const body = res.data || {};
    const raw = body.data && typeof body.data === "object" ? body.data : {};
    const providers = raw.providers && typeof raw.providers === "object" ? raw.providers : {};
    return {
      success: body.success !== false,
      data: {
        systemType: raw.systemType || "unknown",
        accessMethods: Array.isArray(raw.accessMethods) ? raw.accessMethods : [],
        providers: {
          api: providers.api ?? null,
          database: providers.database ?? null,
          vectorStore: providers.vectorStore ?? null,
        },
      },
    };
  } catch (err) {
    if (isCanceledEvidenceRequest(err)) {
      return {
        success: false,
        cancelled: true,
        error: err.message || "cancelled",
        data: { ...EMPTY_DISCOVERY, providers: { ...EMPTY_DISCOVERY.providers } },
      };
    }
    return {
      success: false,
      error: err.response?.data?.error || err.message || "unavailable",
      data: { ...EMPTY_DISCOVERY, providers: { ...EMPTY_DISCOVERY.providers } },
    };
  }
}

/**
 * Saved Chat Intake answers from intake_sessions.
 * Does not open the live intake-agent session.
 * @param {string|number} projectId
 */
export async function fetchReadinessIntakeSnapshot(projectId) {
  const key = String(projectId ?? "");
  if (!key) {
    return {
      success: false,
      error: "projectId is required",
      data: { captured: {}, gate0_data: {}, has_session: false },
    };
  }
  const res = await axios.get(`${API_BASE_URL}/projects/${key}/gate1/intake-snapshot`);
  const body = res.data || {};
  const data = body.data && typeof body.data === "object" ? body.data : {};
  return {
    success: body.success !== false,
    data: {
      captured: data.captured && typeof data.captured === "object" ? data.captured : {},
      gate0_data: data.gate0_data && typeof data.gate0_data === "object" ? data.gate0_data : {},
      has_session: Boolean(data.has_session),
    },
  };
}
